import { useState } from "react";

interface OrgNode {
  id: number;
  parent: number | null;
  text: string;
  icon?: string;
  children?: OrgNode[];
}

interface FlattenedNode {
  id: number;
  positionName: string;
  positionCode: string;
  employeeName: string;
  employeeCode: string;
  reportsToPositionName: string;
  reportsToPositionCode: string;
  reportsToEmployeeName: string;
  reportsToEmployeeCode: string;
}



const buildTree = (nodes: OrgNode[]): OrgNode[] => {
  const map = new Map<number, OrgNode>();
  const roots: OrgNode[] = [];

  nodes.forEach(node => map.set(node.id, { ...node, children: [] }));

  nodes.forEach(node => {
    if (node.parent && map.has(node.parent)) {
      map.get(node.parent)!.children!.push(map.get(node.id)!);
    } else {
      roots.push(map.get(node.id)!);
    }
  });

  return roots;
};

const parseText = (text: string | undefined) => {
  if (!text) return { positionName: '', positionCode: '', employeeName: '', employeeCode: '' };
  
  const [positionPart, employeePart] = text.split("|").map(t => t.trim());
  let positionName = positionPart || '';
  let positionCode = '';
  let employeeName = '';
  let employeeCode = '';

  if (employeePart) {
    const parts = employeePart.split("-").map(p => p.trim());
    positionCode = parts[0] || '';
    employeeName = parts[1] || '';
    employeeCode = parts[2] || '';
  }

  return { positionName, positionCode, employeeName, employeeCode };
};

const flattenTree = (nodes: OrgNode[], parentText = ''): FlattenedNode[] => {
  return nodes.reduce<FlattenedNode[]>((acc, node) => {
    const current = parseText(node.text);
    const reportsTo = parseText(parentText);

    acc.push({
      id: node.id,
      positionName: current.positionName,
      positionCode: current.positionCode,
      employeeName: current.employeeName,
      employeeCode: current.employeeCode,
      reportsToPositionName: reportsTo.positionName,
      reportsToPositionCode: reportsTo.positionCode,
      reportsToEmployeeName: reportsTo.employeeName,
      reportsToEmployeeCode: reportsTo.employeeCode,
    });

    if (node.children && node.children.length) {
      acc.push(...flattenTree(node.children, node.text));
    }

    return acc;
  }, []);
};





interface ListViewProps {
  data: OrgNode[];
}

const ListView: React.FC<ListViewProps> = ({ data }) => {
  const treeData = buildTree(data);
  const flattened = flattenTree(treeData);

  const columnStyle: React.CSSProperties = {
    border: '1px solid #ccc',
    padding: '8px',
    width: '130px',
    whiteSpace: 'nowrap',
    textAlign: 'center',
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 100; // rows per page
  const totalPages = Math.ceil(flattened.length / pageSize);

  const paginatedData = flattened.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handlePrev = () => {
    setCurrentPage((prev:any) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((prev:any) => Math.min(prev + 1, totalPages));
  };

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: 'max-content', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: '#f0f0f0' }}>
            <tr>
              <th style={columnStyle}>Position Name</th>
              <th style={columnStyle}>Position Code</th>
              <th style={columnStyle}>Employee Name</th>
              <th style={columnStyle}>Employee Code</th>
              <th style={columnStyle}>Reports To Position</th>
              <th style={columnStyle}>Reports To Position Code</th>
              <th style={columnStyle}>Reports To Employee</th>
              <th style={columnStyle}>Reports To Employee Code</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => (
              <tr
                key={item.id}
                style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9' }}
              >
                <td style={columnStyle}>{item.positionName}</td>
                <td style={columnStyle}>{item.positionCode}</td>
                <td style={columnStyle}>{item.employeeName}</td>
                <td style={columnStyle}>{item.employeeCode}</td>
                <td style={columnStyle}>{item.reportsToPositionName}</td>
                <td style={columnStyle}>{item.reportsToPositionCode}</td>
                <td style={columnStyle}>{item.reportsToEmployeeName}</td>
                <td style={columnStyle}>{item.reportsToEmployeeCode}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div
        style={{
          marginTop: 10,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <button onClick={handlePrev} disabled={currentPage === 1}>
          Prev
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button onClick={handleNext} disabled={currentPage === totalPages}>
          Next
        </button>
      </div>
    </div>
  );
};

export default ListView
